import { useRef, useState } from 'react';

import {
  useMutation,
  useQueryClient
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  Modal,
  StatusIndicator
} from '@unified-checkout/design-system';
import * as amplitude from '@amplitude/analytics-browser';

import type { IButton } from 'libs/design-system/src/types/Button/button';

import {
  CANCEL,
  CONNECT_ACTION,
  DELETE_STATUS,
  DISCONNECT_CONNECT_MODAL,
  GOOGLE,
  MODAL_STEP_STATUS,
} from '@app/dashboard/constants/Bills';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { THREE_SECONDS } from '@app/dashboard/constants/shared';

import { connectDisconnect } from '@app/dashboard/services/inner_layout/bills/pay_ads/connect-disconnect';

import {
  BILLS_EVENTS,
  BILLS_EVENTS_PARAMS,
} from '@app/dashboard/constants/tracking_events/bills';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';

import type { IConnectDisconnectModal } from '@app/dashboard/types/innerLayout/bills/connect-disconnect';

import { isStatusVisible } from '../../../utils';

import style from './connect-disconnect.module.scss';

export const ConnectDisconnectModal = ({
  showConnectDisconnect,
  setShowConnectDisconnect,
  account,
  action,
}: IConnectDisconnectModal) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string>(MODAL_STEP_STATUS.CONFIRM);
  const actionPayloadRef = useRef<{ action?: string; accountId?: string }>({});

  const {
    mutate: mutateConnectDisconnect,
    isPending: isConnectDisconnectLoading,
    data: connectDisconnectData,
    error: connectDisconnectError,
  } = useMutation({
    mutationFn: (payload: { action: string; account_id: string }) => connectDisconnect(payload),
    onSuccess: (response, variables) => {
      const { action: currentAction, account_id: accountId } = variables;
      const isConnectAction = currentAction === CONNECT_ACTION;

      if (isConnectAction) {
        amplitude.track(BILLS_EVENTS.BILLS_CONNECT_AD_ACCOUNT_SELECTED, {
          [BILLS_EVENTS_PARAMS.AD_PLATFORM]: GOOGLE,
          [BILLS_EVENTS_PARAMS.AD_ACCOUNT_ID]: accountId,
          [BILLS_EVENTS_PARAMS.IS_FIRST_TIME]: false,
          [BILLS_EVENTS_PARAMS.BILL_STATUS]: response?.data?.status,
        });
      } else {
        amplitude.track(BILLS_EVENTS.BILLS_DISCONNECT_AD_ACCOUNT_SELECTED, {
          [BILLS_EVENTS_PARAMS.AD_PLATFORM]: GOOGLE,
          [BILLS_EVENTS_PARAMS.AD_ACCOUNT_ID]: accountId,
          [BILLS_EVENTS_PARAMS.STATUS]: response?.data?.status,
        });
      }

      setStatus(MODAL_STEP_STATUS.SUCCESS);
      queryClient.resetQueries({ queryKey: [QUERY_KEYS.GET_ACCOUNT_DETAILS] });
      setTimeout(() => {
        setShowConnectDisconnect(false);
        setStatus(MODAL_STEP_STATUS.CONFIRM);
      }, THREE_SECONDS);
    },
    onError: (mutationError: any, variables) => {
      const { action: currentAction, account_id: accountId } = variables;
      const isConnectAction = currentAction === CONNECT_ACTION;

      if (isConnectAction) {
        amplitude.track(BILLS_EVENTS.BILLS_CONNECT_AD_ACCOUNT_SELECTED, {
          [BILLS_EVENTS_PARAMS.AD_PLATFORM]: GOOGLE,
          [BILLS_EVENTS_PARAMS.AD_ACCOUNT_ID]: accountId,
          [BILLS_EVENTS_PARAMS.IS_FIRST_TIME]: false,
          [BILLS_EVENTS_PARAMS.BILL_STATUS]: mutationError?.response?.data?.status,
          [SHARED_EVENTS_PARAMS.ERR_DESCRIPTION]: mutationError?.response?.data?.message,
        });
      } else {
        amplitude.track(BILLS_EVENTS.BILLS_DISCONNECT_AD_ACCOUNT_SELECTED, {
          [BILLS_EVENTS_PARAMS.AD_PLATFORM]: GOOGLE,
          [BILLS_EVENTS_PARAMS.AD_ACCOUNT_ID]: accountId,
          [BILLS_EVENTS_PARAMS.STATUS]: mutationError?.response?.data?.status,
          [SHARED_EVENTS_PARAMS.ERR_DESCRIPTION]: mutationError?.response?.data?.message,
        });
      }

      setStatus(MODAL_STEP_STATUS.FAILED);
      setTimeout(() => {
        setShowConnectDisconnect(false);
        setStatus(MODAL_STEP_STATUS.CONFIRM);
      }, THREE_SECONDS);
    }
  });
  const isConnect = action === CONNECT_ACTION;

  const handleConfirm = ({ action, account_id }: { action: string, account_id: string }) => {
    if (!account_id) return;

    actionPayloadRef.current = {
      action,
      accountId: account_id,
    };

    mutateConnectDisconnect({ action, account_id });
  };
  const handleClose = () => {
    if (!showConnectDisconnect) {
      return;
    }

    amplitude.track(BILLS_EVENTS.BILLS_SCREEN_EXITED, {
      [BILLS_EVENTS_PARAMS.SCREEN_NAME]: DISCONNECT_CONNECT_MODAL,
      [BILLS_EVENTS_PARAMS.EXIT_TYPE]: CANCEL,
    });
    setStatus(MODAL_STEP_STATUS.CONFIRM);
    setShowConnectDisconnect(false)
  }
  const getSupportingText = () => {
    if (!isStatusVisible(status)) {
      return isConnect
        ? t('innerLayout.billsPage.payAds.connectAccountConfirmation', { accountName: account?.name })
        : t('innerLayout.billsPage.payAds.disconnectAccountConfirmation', { accountName: account?.name })
    }
    return ""
  }
  const btnProps = !isStatusVisible(status) ? [
    {
      color: isConnect ? "primary" : "danger",
      text: isConnect ? t('innerLayout.billsPage.payAds.connect') : t('innerLayout.billsPage.payAds.disconnect'),
      onClickHandler: () => handleConfirm({ action, account_id: account?.account_id }),
      loading: isConnectDisconnectLoading,
      disabled: isConnectDisconnectLoading,
    } as IButton,
    {
      text: t('form.cancel'),
      variant: "outlined",
      color: "secondary",
      onClickHandler: handleClose,
    } as IButton,
  ] : undefined

  const getStatusContent = () => {
    if (status === MODAL_STEP_STATUS.SUCCESS) {
      return {
        title: connectDisconnectData?.data?.title,
        description: connectDisconnectData?.data?.message,
      };
    }

    if (status === MODAL_STEP_STATUS.FAILED) {
      return {
        title: (connectDisconnectError as any)?.response?.data?.title,
        description: (connectDisconnectError as any)?.response?.data?.message,
      };
    }

    return { title: '', description: '' };
  };

  const { title: statusTitle, description: statusDescription } = getStatusContent();
  const deleteStatusContent = DELETE_STATUS(t).find((item) => item.id === status);

  return (
    <div className={style['modal']}>
      <Modal
        open={showConnectDisconnect}
        onCloseHandler={handleClose}
        title={!isStatusVisible(status) ? (isConnect ? t('innerLayout.billsPage.payAds.connectAccount') : t('innerLayout.billsPage.payAds.disconnectAccount')) : ""}
        supportingText={getSupportingText()}
        hasCloseIcon
        closeOnClickOutside={false}
        btnsProps={btnProps}
      >
        {isStatusVisible(status) &&
          <StatusIndicator
            status={deleteStatusContent?.status}
            icon={deleteStatusContent?.icon}
            text={statusTitle}
            hintText={statusDescription}
          />
        }
      </Modal>
    </div>
  );
};